import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Label } from '../../components/ui/label';
import { Switch } from '../../components/ui/switch';
import { Separator } from '../../components/ui/separator';
import { CreditCard, Crown, AlertCircle, Mail } from 'lucide-react';
import { toast } from 'sonner';
import * as settingsApi from '../endpoints';

export default function NotificationsTab({
  notifications,
  setNotifications
}) {
  const handleNotificationChange = async (key, value) => {
    const newNotifications = { ...notifications, [key]: value };
    setNotifications(newNotifications);

    try {
      await settingsApi.updateNotifications(newNotifications);
    } catch (error) {
      // Revert on error
      setNotifications(notifications);
      toast.error('Failed to update notification settings');
    }
  };

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="font-heading text-lg">Email Notifications</CardTitle>
          <CardDescription>Choose which email notifications you want to receive</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Billing Notifications */}
          <div>
            <h4 className="font-medium mb-4 flex items-center gap-2">
              <CreditCard className="w-4 h-4 text-primary" />
              Billing & Payments
            </h4>
            <div className="space-y-4">
              <div className="flex items-center justify-between p-4 rounded-lg bg-muted/30 hover:bg-muted/50 transition-colors">
                <div>
                  <Label htmlFor="paymentSuccess" className="font-medium cursor-pointer">Successful Payments</Label>
                  <p className="text-sm text-muted-foreground">Get notified when a payment is processed successfully</p>
                </div>
                <Switch
                  id="paymentSuccess"
                  checked={notifications.paymentSuccess}
                  onCheckedChange={(checked) => handleNotificationChange('paymentSuccess', checked)}
                />
              </div>

              <div className="flex items-center justify-between p-4 rounded-lg bg-muted/30 hover:bg-muted/50 transition-colors">
                <div>
                  <Label htmlFor="paymentFailed" className="font-medium cursor-pointer">Failed Payments</Label>
                  <p className="text-sm text-muted-foreground">Get alerted when a payment fails to process</p>
                </div>
                <Switch
                  id="paymentFailed"
                  checked={notifications.paymentFailed}
                  onCheckedChange={(checked) => handleNotificationChange('paymentFailed', checked)}
                />
              </div>

              <div className="flex items-center justify-between p-4 rounded-lg bg-muted/30 hover:bg-muted/50 transition-colors">
                <div>
                  <Label htmlFor="invoiceGenerated" className="font-medium cursor-pointer">Invoice Generated</Label>
                  <p className="text-sm text-muted-foreground">Receive email when a new invoice is created</p>
                </div>
                <Switch
                  id="invoiceGenerated"
                  checked={notifications.invoiceGenerated}
                  onCheckedChange={(checked) => handleNotificationChange('invoiceGenerated', checked)}
                />
              </div>
            </div>
          </div>

          <Separator />

          {/* Subscription Notifications */}
          <div>
            <h4 className="font-medium mb-4 flex items-center gap-2">
              <Crown className="w-4 h-4 text-primary" />
              Subscription
            </h4>
            <div className="space-y-4">
              <div className="flex items-center justify-between p-4 rounded-lg bg-muted/30 hover:bg-muted/50 transition-colors">
                <div>
                  <Label htmlFor="subscriptionRenewal" className="font-medium cursor-pointer">Subscription Renewal</Label>
                  <p className="text-sm text-muted-foreground">Get notified before your subscription renews</p>
                </div>
                <Switch
                  id="subscriptionRenewal"
                  checked={notifications.subscriptionRenewal}
                  onCheckedChange={(checked) => handleNotificationChange('subscriptionRenewal', checked)}
                />
              </div>

              <div className="flex items-center justify-between p-4 rounded-lg bg-muted/30 hover:bg-muted/50 transition-colors">
                <div>
                  <Label htmlFor="subscriptionCancelled" className="font-medium cursor-pointer">Subscription Cancelled</Label>
                  <p className="text-sm text-muted-foreground">Confirmation when your subscription is cancelled</p>
                </div>
                <Switch
                  id="subscriptionCancelled"
                  checked={notifications.subscriptionCancelled}
                  onCheckedChange={(checked) => handleNotificationChange('subscriptionCancelled', checked)}
                />
              </div>
            </div>
          </div>

          <Separator />

          {/* Usage Notifications */}
          <div>
            <h4 className="font-medium mb-4 flex items-center gap-2">
              <AlertCircle className="w-4 h-4 text-primary" />
              Usage Alerts
            </h4>
            <div className="space-y-4">
              <div className="flex items-center justify-between p-4 rounded-lg bg-muted/30 hover:bg-muted/50 transition-colors">
                <div>
                  <Label htmlFor="usageLimitWarning" className="font-medium cursor-pointer">Usage Limit Warning</Label>
                  <p className="text-sm text-muted-foreground">Get alerted when approaching your plan limits</p>
                </div>
                <Switch
                  id="usageLimitWarning"
                  checked={notifications.usageLimitWarning}
                  onCheckedChange={(checked) => handleNotificationChange('usageLimitWarning', checked)}
                />
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      <Card className="border-dashed">
        <CardContent className="p-6">
          <div className="flex items-start gap-4">
            <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center shrink-0">
              <Mail className="w-5 h-5 text-primary" />
            </div>
            <div>
              <h4 className="font-medium mb-1">Email Preferences Note</h4>
              <p className="text-sm text-muted-foreground">
                Critical security and account-related emails will always be sent regardless of these settings.
                You can unsubscribe from marketing emails at any time.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
